/**
 * Pre-meeting briefs — the one-pager you read in the 60 seconds before a
 * call so you walk in knowing exactly where things stand.
 *
 * WHY this exists: the CRM already holds everything you'd want before a
 * meeting — deal state, the relationship history, what was promised last
 * time, why deals like this close — but it's scattered across the record.
 * This composer pulls it into four plain-English sections (deal state,
 * history, open items, talking points) so the prep is one glance, not a
 * five-tab scavenger hunt.
 *
 * Honest accounting: every line is deterministic and computed from live
 * data — the Person record, its activity timeline, the meeting notes from
 * the `meetNotes` seam (real Google Meet when the worker is configured,
 * the labelled simulation otherwise), and the win-loss history. No model,
 * no invention. Empty sections are simply omitted rather than padded.
 */

import type { Person } from '../db/types';
import type { MeetNote } from '../agent/meetNotes';
import { isFollowUpOverdue, lastTouchAt, scoreLead } from './score';
import { computeWinLoss, winLossHeadline, type WinLossSummary } from './winloss';

export interface PreMeetingBrief {
  /** "VP Sales · Brightline" — who you're meeting. */
  subtitle: string;
  /** Where the deal/relationship stands right now. */
  dealState: string[];
  /** Recent meetings and touches, most recent first. */
  history: string[];
  /** What's still outstanding — promises, next step, follow-up. */
  openItems: string[];
  /** Suggested points to raise, grounded in the data above. */
  talkingPoints: string[];
}

const DAY_MS = 86_400_000;

function daysAgoLabel(iso?: string): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
}

const fmtUsd = (n: number): string =>
  n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`;

/** Compose the brief from a Person plus freshly-pulled meeting notes. */
export function composePreMeetingBrief(args: {
  person: Person;
  meetNotes: MeetNote[];
  /** Whole roster, so win-loss can be rolled up; optional. */
  roster?: Person[];
}): PreMeetingBrief {
  const { person, meetNotes } = args;
  const wl: WinLossSummary | null = args.roster
    ? computeWinLoss(args.roster)
    : null;

  const subtitle =
    [person.title, person.company].filter(Boolean).join(' · ') || person.name;

  // --- Deal state ----------------------------------------------------------
  const dealState: string[] = [];
  const score = scoreLead(person);
  if (person.category === 'prospect') {
    const stage = person.stage ?? 'new';
    dealState.push(`Stage: ${stage}${person.source ? ` · via ${person.source}` : ''}`);
    if (person.estimatedValue) {
      dealState.push(`Deal size: ${fmtUsd(person.estimatedValue)}`);
    }
  } else if (person.category === 'customer') {
    dealState.push(`Status: ${person.status ?? 'active'}`);
    if (person.accountValue) {
      dealState.push(`Account value: ${fmtUsd(person.accountValue)}`);
    }
    if (person.renewalDate) dealState.push(`Renewal: ${person.renewalDate}`);
  }
  dealState.push(`Lead score: ${score.score} (${score.band})`);
  const last = daysAgoLabel(lastTouchAt(person));
  if (last) dealState.push(`Last touch: ${last}`);
  if (person.enrichment?.status === 'done' && person.enrichment.summary) {
    dealState.push(`Our read: ${person.enrichment.summary}`);
  }

  // --- History -------------------------------------------------------------
  // Real meeting notes from the seam lead; recent timeline touches fill in.
  const history: string[] = [];
  for (const n of meetNotes.slice(0, 3)) {
    const when = daysAgoLabel(n.at) ?? '';
    history.push(`${n.title}${when ? ` (${when})` : ''} — ${n.summary}`);
  }
  const acts = [...(person.activity ?? [])]
    .filter((a) => a.kind !== 'system')
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  for (const a of acts.slice(0, 4)) {
    const when = daysAgoLabel(a.at) ?? '';
    history.push(`${a.kind}${when ? ` (${when})` : ''}: ${a.text}`);
  }

  // --- Open items ----------------------------------------------------------
  const openItems: string[] = [];
  // Action items promised in the most recent meeting are the freshest debts.
  if (meetNotes[0]?.actionItems?.length) {
    for (const item of meetNotes[0].actionItems) {
      openItems.push(`From last meeting: ${item}`);
    }
  }
  if (person.nextStep) openItems.push(`Next step: ${person.nextStep}`);
  if (person.followUpDate) {
    openItems.push(
      `Follow-up due ${person.followUpDate}${isFollowUpOverdue(person) ? ' — OVERDUE' : ''}`,
    );
  }

  // --- Talking points ------------------------------------------------------
  const talking: string[] = [];
  // 1) Close out anything still open, in your own words.
  if (openItems.length > 0) {
    talking.push('Confirm where the open items above landed before anything new.');
  }
  // 2) Lead with live enrichment signals — shows you did the homework.
  for (const sig of (person.enrichment?.signals ?? []).slice(0, 2)) {
    talking.push(`Reference the signal: ${sig}`);
  }
  // 3) Stage-appropriate move.
  if (person.category === 'prospect') {
    const move: Record<string, string> = {
      new: 'Qualify: budget, authority, need, timing — find the real next step.',
      qualified: 'Push toward a proposal — align on scope and the number that matters.',
      proposal: 'Drive to a decision — surface and answer the last objection.',
      'closed-won': 'Kick off onboarding and set the first success milestone.',
      'closed-lost': 'Understand what changed — is there a re-open path?',
    };
    const m = move[person.stage ?? 'new'];
    if (m) talking.push(m);
  }
  // 4) Lead with what closes deals like this (and pre-empt the usual killer).
  if (wl) {
    const head = winLossHeadline(wl);
    const topWin = wl.wonReasons.find((r) => r.reason !== '(no reason given)');
    if (topWin) {
      talking.push(
        `Lean on our top win driver — ${topWin.reason.toLowerCase()} — it's why teams like this say yes.`,
      );
    } else if (head) {
      talking.push(head);
    }
  }
  // 5) Always have a reason for the meeting to exist.
  if (talking.length === 0) {
    talking.push('Re-establish the relationship and surface the current priority.');
  }

  return { subtitle, dealState, history, openItems, talkingPoints: talking };
}

/** Render a brief as copy-ready markdown (for the clipboard / a doc). */
export function briefToMarkdown(person: Person, brief: PreMeetingBrief): string {
  const section = (title: string, rows: string[]): string =>
    rows.length ? `## ${title}\n${rows.map((r) => `- ${r}`).join('\n')}\n` : '';
  return [
    `# Pre-meeting brief — ${person.name}`,
    `_${brief.subtitle}_\n`,
    section('Deal state', brief.dealState),
    section('History', brief.history),
    section('Open items', brief.openItems),
    section('Talking points', brief.talkingPoints),
  ]
    .filter(Boolean)
    .join('\n');
}
